/* eslint-disable @typescript-eslint/no-explicit-any */
import DocumentHeader from '@/components/common/DocumentHeader';
import React, { useEffect } from 'react';
import { PdfViewer } from 'fluent-components';
import { useNavigate } from 'react-router-dom';
import { Loader } from 'lucide-react';
import ValidateForm from '@/components/common/ValidateForm';
import { useQuery } from '@apollo/client';
import {
  SINGLE_TASK_QUERY,
  TASK_QUERY,
} from '@/graphql/queries/record-management';
import useUserInfo from '@/components/Authenticate/useUserInfo';
import {
  createTaskPayload,
  getPractitionerId,
  getTaskId,
  removeNullAndEmptyObjects,
  removeTypeName,
} from '@/lib/utils';
import { Task } from '@/types/recordManagement';
import { boomWorkflow, taskQueryBusinessStatus } from '@/constants';
import { documentReferenceAPIs } from '@/lib/axios/apis/recordManagement/document';
import EmptyStateScreen from '../EmptyStateScreen';
import { Escalation } from '@/types/doctorManagementTypes';
// import { createAuditEvent } from '@/components/common/ValidateForm/fhirTaskBundle';

const ValidateReport: React.FC<{
  isSupervisor?: boolean;
  escalatedData?: Escalation;
  description?: string;
  showHeader?: boolean;
}> = ({ isSupervisor, escalatedData, description, showHeader = true }) => {
  // TO-DO: Need to use filteredData in place of data mapped in UI and function where it's not mapped in this
  // const [filteredData, setFilteredData] = useState<Task[]>();

  const navigate = useNavigate();
  const { userInfo } = useUserInfo();
  const practitionerId = getPractitionerId(userInfo);

  const queryVariables = isSupervisor
    ? {
        id: escalatedData?.id,
      }
    : {
        business_status:
          taskQueryBusinessStatus.documentValidatorBusinessStatus.toString(),
        owner: `Practitioner/${practitionerId}`,
        fallback_business_status:
          taskQueryBusinessStatus.documentValidatorFallbackBusinessStatus.toString(),
      };
  const { data, loading } = useQuery(
    isSupervisor ? SINGLE_TASK_QUERY : TASK_QUERY,
    { variables: queryVariables, errorPolicy: 'all' }
  );

  const taskDetails = data?.primary?.[0] ?? data?.fallback?.[0] ?? {};
  // Task Queuing, assigning practitioner and checking for existing tasks
  useEffect(() => {
    const processTaskData = async (taskData: Task[], status: string) => {
      if (!taskData?.length) return;
      const taskId = getTaskId(taskData);
      // const practitionerReference = getPractitionerId(userInfo) ?? '';
      console.log(practitionerId, 'practitionerId');

      const payload = createTaskPayload(
        '',
        status,
        taskData[0],
        `Practitioner/${practitionerId}`
      );

      // const auditEventPayload = createAuditEvent(
      //   'amend',
      //   taskDetails,
      //   practitionerReference,
      //   isSupervisor
      // );
      if (taskId && payload && practitionerId) {
        // await AuditEventAPI.postAuditEvent(auditEventPayload?.resource);
        await documentReferenceAPIs.updateTask(
          taskId,
          removeNullAndEmptyObjects(removeTypeName(payload))
        );
      }
    };

    if (data?.primary?.length) {
      processTaskData(
        data.primary,
        isSupervisor
          ? boomWorkflow?.boomSupervisor?.validationInProgress
          : boomWorkflow?.boomValidator?.['inProgress']
      );
    } else if (data?.fallback?.length) {
      (async () => {
        //TODO: Need to manage supervisior flow for business status change

        //Note: - We need it to mark is task is moved into progress
        await processTaskData(
          data.fallback,
          isSupervisor
            ? boomWorkflow?.boomSupervisor?.validationStarted
            : boomWorkflow?.boomValidator?.['started']
        );
        await processTaskData(
          data.fallback,
          isSupervisor
            ? boomWorkflow?.boomSupervisor?.validationInProgress
            : boomWorkflow?.boomValidator?.['inProgress']
        );
      })();
    }
    // TO-DO: Remove this. Need to use filteredData in place of data mapped in UI and function where it's not mapped in this
  }, [data, practitionerId, isSupervisor]);

  if (data?.primary?.length === 0 && data?.fallback?.length === 0) {
    return <EmptyStateScreen message="validate" />;
  }
  if (loading) {
    return <Loader className="fixed left-1/2 top-1/2 animate-spin h-10 w-10" />;
  }

  return (
    <div className="overflow-hidden">
      {showHeader && (
        <DocumentHeader
          onPauseHandler={() =>
            navigate(
              `/review-paused?validator_id=${practitionerId}&document_id=${
                taskDetails?.focus?.reference?.split('/')?.[1]
              }`
            )
          }
          source={isSupervisor ? 'supervisor' : 'validator'}
          taskDetails={taskDetails}
        />
      )}
      <div className="px-14 py-6 flex flex-row bg-white rounded-lg shadow-lg h-screen">
        <div className="w-5/12 flex-shrink-0">
          <PdfViewer
            file={taskDetails?.focus?.resource?.content?.[0]?.attachment}
          />
        </div>
        <div className="w-7/12 flex-shrink-0 px-4 gap-y-8 ">
          <ValidateForm
            isSupervisor={isSupervisor}
            data={taskDetails}
            description={description}
          />
        </div>
      </div>
    </div>
  );
};

export default ValidateReport;
